import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { z } from 'zod';
import type { FornecedorResponse, ParceiroResumoResponse } from '@triade/contracts';
import { useCriarCliente } from '@/api/clientes';
import { useAtualizarFornecedor, useCriarFornecedor, useFornecedor, useFornecedores } from '@/api/fornecedores';
import { conflitoDocumento, useParceiroPorDocumento } from '@/api/parceiros';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import {
  AvisoDocumentoCadastrado,
  ChipsPapeis,
  ParceiroBlocos,
  dadosParceiroAtualizar,
  dadosParceiroCriar,
  schemaFormParceiro,
  valoresDoParceiro,
  valoresNovoParceiro,
  type ParceiroFormValores,
} from '@/components/parceiro';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { useRolarAoRegistro } from '@/hooks/use-rolar-ao-registro';
import { Input } from '@/components/ui/input';
import { useDebounced } from '@/hooks/use-debounced';
import { BotaoRelatorios } from '@/relatorios';
import { ApiError } from '@/lib/api';
import { SeloProtheus, TITULO_CAMPO_PROTHEUS, campoDoProtheus, camposDoProtheusDe } from '@/components/integracoes-erp/selo-protheus';
import { formatarContagem, formatarData } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { mascaraDocumento } from './documento';

export type SituacaoFornecedores = 'ATIVOS' | 'INATIVOS' | 'TODOS';
const SITUACOES: Record<SituacaoFornecedores, string> = { ATIVOS: 'Ativos', INATIVOS: 'Inativos', TODOS: 'Todos' };

/** Formulario do fornecedor: o do parceiro + o que so o papel de fornecedor tem (ativo e optante do Simples). */
const schemaFornecedor = schemaFormParceiro('Informe o nome do fornecedor').extend({ ativo: z.boolean(), optanteSimples: z.boolean() });
type FormFornecedor = z.infer<typeof schemaFornecedor>;

/** Estado da tela na URL (busca, situacao, fornecedor aberto, novo). Filtro so de apresentacao sobre o que a API devolve. */
export interface BuscaFornecedores {
  q?: string;
  /** Chave com prefixo `f`: o router junta as buscas de todas as rotas e um nome igual (`situacao`) com outro tipo quebra a tipagem. */
  fsituacao?: Exclude<SituacaoFornecedores, 'ATIVOS'>;
  fornecedor?: string;
  novo?: true;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela (o router faz JSON.parse da URL). */
export function lerBuscaFornecedores(bruto: Record<string, unknown>): BuscaFornecedores {
  const texto = typeof bruto.q === 'number' ? String(bruto.q) : bruto.q;
  const q = typeof texto === 'string' && texto.trim() ? texto.trim().slice(0, 100) : undefined;
  const situacao = bruto.fsituacao === 'INATIVOS' || bruto.fsituacao === 'TODOS' ? bruto.fsituacao : undefined;
  const fornecedor = typeof bruto.fornecedor === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.fornecedor) ? bruto.fornecedor : undefined;
  const novo = bruto.novo === true || bruto.novo === 'true' ? true : undefined;
  return {
    ...(q ? { q } : {}),
    ...(situacao ? { fsituacao: situacao } : {}),
    ...(fornecedor && !novo ? { fornecedor } : {}),
    ...(novo ? { novo } : {}),
  };
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const CHIP = 'rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body';

/** Tela "Fornecedores" (/cadastros/fornecedores): lista com busca e situacao, e formulario com a barra de registro. */
export function FornecedoresPage({ busca }: { busca: BuscaFornecedores }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('custoAquisicao.editar');
  /** cliente.editar: pode tornar o fornecedor tambem cliente. */
  const podeCliente = pode('cliente.editar');
  const situacao = busca.fsituacao ?? 'ATIVOS';

  /** `semBloqueio`: so a sincronizacao do `q` do debounce (troca o parametro da mesma tela, nada se perde); o resto segue o bloqueio. */
  const mudar = (mudanca: Partial<BuscaFornecedores>, opcoes?: { semBloqueio?: boolean }) => {
    void navigate({ to: '/cadastros/fornecedores', search: { ...busca, ...mudanca }, replace: true, ...(opcoes?.semBloqueio ? { ignoreBlocker: true } : {}) });
  };
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtrasado !== (busca.q ?? '')) mudar({ q: termoAtrasado || undefined }, { semBloqueio: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtrasado]);

  const lista = useFornecedores({
    ...(termoAtrasado ? { q: termoAtrasado } : {}),
    ...(situacao === 'TODOS' ? {} : { ativo: situacao === 'ATIVOS' }),
    limit: 200,
  });
  const fornecedores = lista.data ?? [];
  const pagFornecedores = usePaginarLista(fornecedores);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os fornecedores.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroFormacao erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os fornecedores" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando fornecedores…</Mensagem>;
  else if (fornecedores.length === 0) {
    const filtrado = Boolean(busca.q) || situacao !== 'ATIVOS';
    corpo = filtrado ? (
      <Mensagem papel="status">
        Nenhum fornecedor com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/cadastros/fornecedores', search: {}, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum fornecedor cadastrado.
        {podeEditar && (
          <>
            {' '}
            <Link to="/cadastros/fornecedores" search={{ novo: true }} className="font-medium text-primary hover:underline">
              Cadastre o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-3 py-2.5 font-medium">CPF/CNPJ</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Cadastrado em</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Simples</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagFornecedores.visiveis.map((f) => (
              <tr
                key={f.id}
                data-testid="linha-fornecedor"
                onClick={() => void navigate({ to: '/cadastros/fornecedores', search: (a) => ({ ...a, fornecedor: f.id, novo: undefined }) })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.fornecedor === f.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">
                  <span className="flex flex-wrap items-center gap-2">
                    {f.nome}
                    {f.doProtheus && <SeloProtheus />}
                  </span>
                </td>
                <td className="tabular px-3 py-3 text-ink-secondary">{f.documento ? mascaraDocumento(f.documento) : '—'}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{formatarData(f.createdAt)}</td>
                <td className="px-3 py-3">
                  {f.optanteSimples ? <span data-testid="selo-simples" className={CHIP}>Simples</span> : <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', f.ativo ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                    {f.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Fornecedores
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Fornecedores</h1>
        </div>
      </div>

      <section aria-label="Lista de fornecedores" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
            <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
            <label htmlFor="forn-q" className="sr-only">
              Buscar fornecedor
            </label>
            <input id="forn-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Nome ou documento" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="forn-situacao" className="text-small text-ink-secondary">
              Situação
            </label>
            <select id="forn-situacao" value={situacao} onChange={(e) => mudar({ fsituacao: e.target.value === 'ATIVOS' ? undefined : (e.target.value as 'INATIVOS' | 'TODOS') })} className={CAMPO}>
              {(Object.keys(SITUACOES) as SituacaoFornecedores[]).map((s) => (
                <option key={s} value={s}>
                  {SITUACOES[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {corpo}
        <div className="border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-fornecedores">
          {lista.data ? `${formatarContagem(fornecedores.length)} fornecedor${fornecedores.length === 1 ? '' : 'es'} na lista` : ' '}
        </div>
      </section>

      {(busca.novo || busca.fornecedor) && (
        <FornecedorDetalhe key={busca.fornecedor ?? 'novo'} id={busca.fornecedor} busca={busca} podeEditar={podeEditar} podeCliente={podeCliente} />
      )}

      {/* Com o registro aberto a RecordToolbar (Salvar) ocupa o rodapé: a barra da lista ficaria por cima. */}
      {!busca.novo && !busca.fornecedor && (
        <BarraDeAcoesLista
          novo={podeEditar ? { rotulo: 'Novo fornecedor', onClick: () => void navigate({ to: '/cadastros/fornecedores', search: { ...busca, fornecedor: undefined, novo: true } }) } : undefined}
          paginacao={lista.data ? pagFornecedores.paginacao : undefined}
          relatorios={<BotaoRelatorios tela="fornecedores" />}
        />
      )}
    </div>
  );
}

function valoresDoFornecedor(f: FornecedorResponse): FormFornecedor {
  return { ...valoresDoParceiro(f), ativo: f.ativo, optanteSimples: f.optanteSimples };
}

/** Formulario de um fornecedor (ou novo) com a barra de registro. Sem permissao de gravacao, so leitura. */
function FornecedorDetalhe({ id, busca, podeEditar, podeCliente }: { id: string | undefined; busca: BuscaFornecedores; podeEditar: boolean; podeCliente: boolean }) {
  const navigate = useNavigate();
  const doc = useFornecedor(id);
  const criar = useCriarFornecedor();
  const rolagem = useRolarAoRegistro<HTMLElement>();
  const atualizar = useAtualizarFornecedor();
  const criarCliente = useCriarCliente();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  /** Documento ja cadastrado para outro parceiro (409 ao gravar): mostra o aviso com "usar o parceiro existente". */
  const [conflito, setConflito] = useState<{ documento: string; nome: string } | null>(null);
  const resumoConflito: ParceiroResumoResponse | null = useParceiroPorDocumento(conflito?.documento ?? null).data?.parceiro ?? null;
  /** Cliente criado a partir deste fornecedor ("Também é cliente"). */
  const [clienteCriadoId, setClienteCriadoId] = useState<string | null>(null);
  const [erroPapel, setErroPapel] = useState<string | null>(null);
  // Criado: a navegacao para o registro novo sai DEPOIS do render (o aviso de saida ja enxerga o formulario limpo).
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const base = useMemo<FormFornecedor>(() => (doc.data ? valoresDoFornecedor(doc.data) : { ...valoresNovoParceiro(), ativo: true, optanteSimples: false }), [doc.data]);
  const form = useForm<FormFornecedor>({ resolver: zodResolver(schemaFornecedor), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const bloqueio = useBlockerDirty(isDirty && criadoId === null);
  useEffect(() => {
    if (criadoId) void navigate({ to: '/cadastros/fornecedores', search: (a) => ({ ...a, fornecedor: criadoId, novo: undefined }), replace: true });
  }, [criadoId, navigate]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length;
  const lista = { to: '/cadastros/fornecedores' as const, search: { ...(busca.q ? { q: busca.q } : {}), ...(busca.fsituacao ? { fsituacao: busca.fsituacao } : {}) } };

  async function gravar(v: FormFornecedor) {
    setErroSalvar(null);
    setConflito(null);
    try {
      if (id) {
        const gravado = await atualizar.mutateAsync({ id, ...dadosParceiroAtualizar(v), ativo: v.ativo, optanteSimples: v.optanteSimples });
        form.reset(valoresDoFornecedor(gravado));
      } else {
        const criado = await criar.mutateAsync({ ...dadosParceiroCriar(v), optanteSimples: v.optanteSimples });
        form.reset(valoresDoFornecedor(criado));
        setSalvoEm(new Date());
        setCriadoId(criado.id);
        return;
      }
      setSalvoEm(new Date());
    } catch (e) {
      const dup = conflitoDocumento(e);
      if (dup) setConflito({ documento: v.documento, nome: dup.nome });
      else setErroSalvar(mensagemApi(e));
    }
  }

  /** "Usar o parceiro existente": se ja e fornecedor, abre o fornecedor; senao cria o fornecedor com o parceiro existente e abre. */
  async function usarExistente(r: ParceiroResumoResponse) {
    setErroSalvar(null);
    setConflito(null);
    if (r.fornecedorId) {
      void navigate({ to: '/cadastros/fornecedores', search: (a) => ({ ...a, fornecedor: r.fornecedorId ?? undefined, novo: undefined }), ignoreBlocker: true });
      return;
    }
    try {
      const criado = await criar.mutateAsync({ parceiroId: r.id });
      setCriadoId(criado.id);
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  async function tambemCliente() {
    if (!doc.data) return;
    setErroPapel(null);
    try {
      const cliente = await criarCliente.mutateAsync({ parceiroId: doc.data.parceiroId });
      setClienteCriadoId(cliente.id);
    } catch (e) {
      setErroPapel(mensagemApi(e));
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroFormacao erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir o fornecedor" /></section>;
  if (id && !doc.data) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando fornecedor…</section>;

  return (
    <>
      <section ref={rolagem} aria-label="Dados do fornecedor" className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-body font-semibold">{id ? (doc.data?.nome ?? 'Fornecedor') : 'Novo fornecedor'}</h2>
          {doc.data?.doProtheus && <SeloProtheus />}
        </div>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar os fornecedores, não alterá-los.
          </p>
        )}
        {doc.data && (
          <div className="mt-4 flex flex-col gap-3">
            <ChipsPapeis papeis={doc.data.papeis} />
            {podeCliente && !doc.data.papeis.includes('CLIENTE') && clienteCriadoId === null && (
              <div>
                <button
                  type="button"
                  className="rounded-md border border-input px-3 py-1.5 text-small font-medium text-primary hover:bg-surface-muted disabled:opacity-50"
                  onClick={() => void tambemCliente()}
                  disabled={criarCliente.isPending}
                >
                  Também é cliente
                </button>
              </div>
            )}
            {clienteCriadoId && (
              <p role="status" className="text-small text-ink-body">
                Agora também é cliente.{' '}
                <Link to="/clientes" search={{ id: clienteCriadoId }} className="font-medium text-primary hover:underline">
                  Abrir o cliente
                </Link>
              </p>
            )}
            {erroPapel && (
              <p role="alert" className="text-caption text-danger-ink">
                {erroPapel}
              </p>
            )}
          </div>
        )}
        <fieldset className="mt-4">
          <ParceiroBlocos
            form={form}
            papel="FORNECEDOR"
            parceiroId={doc.data?.parceiroId}
            camposDoProtheus={camposDoProtheusDe('Fornecedor', doc.data?.doProtheus)}
            onUsarExistente={(r) => void usarExistente(r)}
            desabilitado={!podeEditar}
          />
        </fieldset>
        <fieldset disabled={!podeEditar} className="mt-6 flex flex-col gap-4">
          <legend className="text-sm font-semibold text-ink">Como fornecedor</legend>
          <label htmlFor="f-ativo" className="flex cursor-pointer items-center gap-2">
            <input
              id="f-ativo"
              type="checkbox"
              disabled={!id || campoDoProtheus('Fornecedor', doc.data?.doProtheus, 'ativo')}
              title={campoDoProtheus('Fornecedor', doc.data?.doProtheus, 'ativo') ? TITULO_CAMPO_PROTHEUS : undefined}
              className="size-[18px] accent-primary"
              {...form.register('ativo')}
            />
            Ativo
            {!id && <span className="text-small text-ink-muted">Todo fornecedor novo entra ativo.</span>}
          </label>
          <div>
            <label htmlFor="f-simples" className="flex cursor-pointer items-center gap-2">
              <input id="f-simples" type="checkbox" aria-describedby="f-simples-ajuda" className="size-[18px] accent-primary" {...form.register('optanteSimples')} />
              Optante do Simples Nacional
            </label>
            <p id="f-simples-ajuda" className="mt-1 text-caption text-ink-muted">O crédito de CBS/IBS na aquisição será calculado quando as tabelas forem publicadas.</p>
          </div>
        </fieldset>
        {errors.nome && (
          <p role="alert" className="mt-3 text-caption text-danger-ink">
            {errors.nome.message}
          </p>
        )}
        {conflito && (
          <AvisoDocumentoCadastrado nome={conflito.nome} onUsar={() => resumoConflito && void usarExistente(resumoConflito)} desabilitado={!resumoConflito} />
        )}
        {erroSalvar && (
          <p role="alert" className="mt-3 text-caption text-danger-ink">
            {erroSalvar}
          </p>
        )}
        <p className="mt-3 text-caption text-ink-muted">
          Fornecedor é dado de custo: quem vê custo vê a lista; quem altera precisa de permissão de custo de aquisição. Fornecedor inativo não entra em novas vigências.
        </p>
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onList={() => void navigate({ ...lista, replace: true })}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/cadastros/fornecedores', search: (a) => ({ ...a, fornecedor: undefined, novo: true }) })}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
            setConflito(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
